/**
 * 레시피 스텝 어휘 — **지금 유효한 이름의 전수**와 **은퇴 이름 → 새 이름** 대응표. 순수 표 + 순수 함수.
 *
 * 🔴 **왜 있나**(2026-09-27 실측). 9/22 에 만든 레시피로 배치를 시작하자 곧바로 `paused` 로 멎었다.
 *    코어 `app/services/module_steps.py:109-120 _schema()` 가 「시퀀스 `centrifuge.pick` 의 파라미터
 *    계약이 없다」로 거부했다 — 9/24 에 원심·조립의 `pick`·`place` 가 **레시피 묶음**
 *    `pickup`·`placedown` 으로 바뀌었는데(`composites/*.schema.json`) 옛 레시피 본문은 옛 이름 그대로였다.
 *    로컬 레시피 17건 전부가 그랬다.
 *
 * 🔴 **추론이 아니라 표 대조다.** 이 파일은 «이 이름이 돌 것인가»를 판정하지 않는다 — 그 권위는 서버다
 *    (시작 게이트 · 전개기). 여기는 코어 생성물의 파일 이름과 정본 은퇴 원장을 **옮겨 적은 것**뿐이다.
 *    코어가 움직이면 이 표도 손으로 따라간다 — 시험(`recipeStepVocabulary.test.ts`)이 표의 모양을 잡는다.
 */

/** 레시피가 모듈 스텝을 담는 절과 그 키. `recipe.schema.json` 의 `$defs/*Params` 중 `items` 가 `ModuleStep` 인 속성.
 *  🔴 절마다 키가 다르다 — 원심·조립은 `steps`, 염색기는 `module_steps`(염색기의 `steps` 는 **공정 스텝**이라 시퀀스가 아니다).
 *  근거: `ctcells-core/app/schemas/generated/recipe.schema.json` `CentrifugeParams.steps` · `AssemblerParams.steps` ·
 *  `StainingParams.module_steps` · 같은 판정을 코어가 `module_steps.py:123-150 _step_keys()` 로 한다. */
export const RECIPE_STEP_SECTIONS = {
  centrifuge: 'steps',
  assembler: 'steps',
  staining: 'module_steps',
} as const

export type RecipeStepSection = keyof typeof RECIPE_STEP_SECTIONS

/**
 * 지금 유효한 `<절>.<시퀀스>` 전수 — 코어 생성물 `app/schemas/generated/sequences/*.schema.json`(모듈 원자)과
 * `composites/*.schema.json`(레시피 묶음 · L2 가 원자로 편다)의 **파일 이름** 그대로다(ctcells-core `bc75e82` · 2026-09-26).
 * 코어는 이 둘 중 하나에 파일이 없으면 거부한다(`module_steps.py:91-120` `composite_schema` → `_schema`).
 *
 * ⚠ 6축(`collaborative_6axis`)은 뺐다 — **레시피에 6축 절이 없다**(`recipe.schema.json` 최상위 속성 · 정본
 *   `12_레시피명세.md:60 RCP-VER-03` 「v4.4 … 6축 절 전체를 뺀 판」 · `:106 RCP-MSTEP-08`).
 */
export const RECIPE_STEP_VOCABULARY: Readonly<Record<RecipeStepSection, readonly string[]>> = {
  centrifuge: [
    // composites/
    'pickup',
    'placedown',
    // sequences/
    'close_door',
    'grip',
    'gripper_set',
    'inspect',
    'move_to',
    'move_z',
    'open_door',
    'release',
    'rotate',
    'run_centrifugation',
    'slide_tray',
    'wait_for_manual_load',
  ],
  assembler: [
    // composites/
    'invert_drain',
    'pickup',
    'placedown',
    // sequences/
    'close_door',
    'expose_uv',
    'grip',
    'gripper_set',
    'inspect',
    'move_to',
    'move_z',
    'open_door',
    'release',
    'rotate',
    'slide_tray',
  ],
  staining: [
    'aspirate',
    'close_door',
    'discard_tip',
    'dispense_reagent',
    'draw',
    'expel',
    'inspect',
    'mix',
    'mount_tip',
    'move_to',
    'move_z',
    'open_door',
    'set_reagent_storage_temp',
    'shake',
    'slide_tray',
    'wait_with_temp_monitoring',
  ],
}

/**
 * 은퇴 시퀀스 이름 → 새 이름. **값만 바뀌고 칸은 그대로다** — 그래서 이름만 갈아 끼운다.
 *
 * - 원심·조립 `pick`→`pickup` · `place`→`placedown` — 정본 은퇴 원장 `specs/ledger/retired_ids.yaml:1225-1280`
 *   (`reverify: 불요 — 칸 그대로 묶음 정의로 옮김(값 변경 0)`) · `12_레시피명세.md:60 RCP-VER-03` 「v4.5 …
 *   원심·조립 `sequence` `pick`→`pickup`(56) · `place`→`placedown`(56)」
 * - 염색 `move_gantry_z`→`move_z` — `retired_ids.yaml:429-432` · `08_장비·드라이버.md:231` 「`move_gantry_z` 는
 *   폐기하고 `move_z` 로 개명」(칸 `target_mm`·`speed_mm_s`·`reason` 같음)
 *
 * 🔴 **`grip`·`release` 로 옮기지 않는다.** 원장이 `replaced_by` 에 원자(`centrifuge.grip`)를 같이 적었지만
 *    그건 «모듈이 받는 원자»의 이름이고, 레시피 스텝(묶음 칸 `payload_kind`·`source`)을 그대로 옮길 곳은 묶음이다.
 */
export const RETIRED_STEP_RENAMES: Readonly<
  Record<RecipeStepSection, Readonly<Record<string, string>>>
> = {
  centrifuge: { pick: 'pickup', place: 'placedown' },
  assembler: { pick: 'pickup', place: 'placedown' },
  staining: { move_gantry_z: 'move_z' },
}

/**
 * 은퇴 **파라미터 값** — 이름이 아니라 값이 바뀐 것. 같은 v4.5 판에서 함께 바뀌었다.
 * 염색 `slide_tray` `target` `pipetting`→`work` — `12_레시피명세.md:60 RCP-VER-03` 「염색 `slide_tray` `target`
 * `pipetting`→`work`(슬라이드 위치 통일)」 · `retired_ids.yaml:1336-1339 staining.pipetting_zone → staining.slide_work`.
 */
export const RETIRED_PARAM_VALUES: readonly {
  section: RecipeStepSection
  sequence: string
  param: string
  from: string
  to: string
}[] = [
  { section: 'staining', sequence: 'slide_tray', param: 'target', from: 'pipetting', to: 'work' },
]

/** 레시피 본문에서 **은퇴한 절**. 스키마에 없어 코어가 걷지 않는다(`_step_keys()` 가 스키마에서 절을 찾는다). */
export const RETIRED_RECIPE_SECTIONS = ['collaborative_6axis'] as const

/** 한 건의 변경 — `where` 는 JSON 포인터(`/centrifuge/steps/3/sequence`) */
export interface RecipeStepChange {
  where: string
  from: string
  to: string
}

/** 스캔 결과 한 건 — 옛 이름 · 모르는 이름 · 은퇴 절 */
export type RecipeVocabularyFinding =
  | {
      kind: 'retired'
      section: RecipeStepSection
      sequence: string
      replacement: string
      count: number
    }
  | {
      kind: 'retiredValue'
      section: RecipeStepSection
      sequence: string
      param: string
      value: string
      replacement: string
      count: number
    }
  | { kind: 'unknown'; section: RecipeStepSection; sequence: string; count: number }
  | { kind: 'retiredSection'; section: string }

type Json = unknown

function isRecord(value: Json): value is Record<string, Json> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stepsOf(payload: Json, section: RecipeStepSection): Json[] {
  if (!isRecord(payload)) return []
  const body = payload[section]
  if (!isRecord(body)) return []
  const list = body[RECIPE_STEP_SECTIONS[section]]
  return Array.isArray(list) ? list : []
}

const SECTIONS = Object.keys(RECIPE_STEP_SECTIONS) as RecipeStepSection[]

/**
 * 본문을 표와 대조한다. **바꾸지 않는다.** 같은 이름은 한 건으로 묶어 `count` 를 센다.
 * 🔴 판정 재료는 이 파일의 표뿐이다 — 상태·모듈 응답을 보지 않는다.
 */
export function scanRecipeVocabulary(payload: Json): RecipeVocabularyFinding[] {
  const out: RecipeVocabularyFinding[] = []
  for (const section of SECTIONS) {
    const valid = new Set(RECIPE_STEP_VOCABULARY[section])
    const renames = RETIRED_STEP_RENAMES[section]
    const retired = new Map<string, number>()
    const unknown = new Map<string, number>()
    const values = new Map<string, number>()
    for (const step of stepsOf(payload, section)) {
      if (!isRecord(step) || typeof step.sequence !== 'string') continue
      const sequence = step.sequence
      if (Object.hasOwn(renames, sequence)) retired.set(sequence, (retired.get(sequence) ?? 0) + 1)
      else if (!valid.has(sequence)) unknown.set(sequence, (unknown.get(sequence) ?? 0) + 1)
      RETIRED_PARAM_VALUES.forEach((rule, index) => {
        if (rule.section !== section || rule.sequence !== sequence) return
        if (isRecord(step.params) && step.params[rule.param] === rule.from) {
          values.set(String(index), (values.get(String(index)) ?? 0) + 1)
        }
      })
    }
    for (const [sequence, count] of retired) {
      out.push({ kind: 'retired', section, sequence, replacement: renames[sequence] ?? '', count })
    }
    for (const [index, count] of values) {
      const rule = RETIRED_PARAM_VALUES[Number(index)]
      if (rule === undefined) continue
      out.push({
        kind: 'retiredValue',
        section,
        sequence: rule.sequence,
        param: rule.param,
        value: rule.from,
        replacement: rule.to,
        count,
      })
    }
    for (const [sequence, count] of unknown) out.push({ kind: 'unknown', section, sequence, count })
  }
  if (isRecord(payload)) {
    for (const section of RETIRED_RECIPE_SECTIONS) {
      if (section in payload) out.push({ kind: 'retiredSection', section })
    }
  }
  return out
}

/**
 * 은퇴 이름을 새 이름으로 **바꾸기만** 한다 — 새 객체를 돌려주고 원본은 건드리지 않는다.
 *
 * 🔴 **그 외 키는 한 글자도 안 건드린다**(`CLAUDE.md` 「PUT 은 받은 것 위에 고친 칸만」과 같은 규율).
 *    화면이 모르는 키 · 은퇴 절(`collaborative_6axis`) · 모르는 시퀀스 이름도 그대로 둔다 — 그것들은
 *    이름 바꾸기로 풀리는 종류가 아니다(`scanRecipeVocabulary` 가 따로 알린다).
 */
export function migrateRecipeSteps<T>(payload: T): { payload: T; changed: RecipeStepChange[] } {
  const changed: RecipeStepChange[] = []
  if (!isRecord(payload)) return { payload, changed }
  const next: Record<string, Json> = { ...payload }
  for (const section of SECTIONS) {
    const body = next[section]
    const key = RECIPE_STEP_SECTIONS[section]
    if (!isRecord(body) || !Array.isArray(body[key])) continue
    const renames = RETIRED_STEP_RENAMES[section]
    let touched = false
    const steps = (body[key] as Json[]).map((step, index) => {
      if (!isRecord(step) || typeof step.sequence !== 'string') return step
      let out = step
      const from = step.sequence
      const to = Object.hasOwn(renames, from) ? renames[from] : undefined
      if (to !== undefined) {
        out = { ...out, sequence: to }
        changed.push({ where: `/${section}/${key}/${String(index)}/sequence`, from, to })
      }
      for (const rule of RETIRED_PARAM_VALUES) {
        if (rule.section !== section || rule.sequence !== out.sequence) continue
        const params = out.params
        if (!isRecord(params) || params[rule.param] !== rule.from) continue
        out = { ...out, params: { ...params, [rule.param]: rule.to } }
        changed.push({
          where: `/${section}/${key}/${String(index)}/params/${rule.param}`,
          from: rule.from,
          to: rule.to,
        })
      }
      if (out !== step) touched = true
      return out
    })
    if (touched) next[section] = { ...body, [key]: steps }
  }
  return { payload: (changed.length === 0 ? payload : next) as T, changed }
}
